<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">إشعارات الطلاب</h1>
          <p class="ds-page-header__subtitle">إرسال إشعار داخل التطبيق لجميع الطلاب أو لطلاب سنة دراسية محددة</p>
        </div>
      </div>

      <div class="ds-card ds-card--form">
        <div class="form-field">
          <label class="field-label">العنوان</label>
          <input type="text" class="form-control" v-model="form.title" placeholder="عنوان الإشعار" maxlength="255" />
        </div>

        <div class="form-field mt-3">
          <label class="field-label">نص الإشعار</label>
          <textarea class="form-control" rows="4" v-model="form.message" placeholder="نص الإشعار" maxlength="2000"></textarea>
        </div>

        <div class="form-field mt-3">
          <label class="field-label">السنة الدراسية المستهدفة</label>
          <div class="d-flex gap-3 flex-wrap mb-2">
            <label class="target-option">
              <input type="radio" value="all" v-model="form.target_type" />
              <span>كل الطلاب</span>
            </label>
            <label class="target-option">
              <input type="radio" value="academic_year" v-model="form.target_type" />
              <span>سنة دراسية محددة</span>
            </label>
          </div>
          <select
            v-if="form.target_type === 'academic_year'"
            class="form-select"
            v-model="form.academic_year_id"
          >
            <option :value="null" disabled>اختر السنة الدراسية...</option>
            <option v-for="y in gradeStore.activeGrades" :key="y.id" :value="y.id">{{ y.name }}</option>
          </select>
        </div>

        <div class="mt-4 text-end">
          <button class="ds-btn ds-btn--primary" :disabled="!canSend || sending" @click="confirmSend">
            <i class="pi pi-spin pi-spinner" v-if="sending"></i>
            <i class="pi pi-send" v-else></i>
            {{ sending ? 'جارٍ الإرسال...' : 'إرسال' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

function blankForm() {
  return { title: '', message: '', target_type: 'all', academic_year_id: null };
}

export default {
  name: 'StudentNotificationsIndex',
  components: { Breadcrumb },

  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'إشعارات الطلاب', path: '/student-notifications' }],
      form: blankForm(),
      sending: false,
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },

    canSend() {
      if (!this.form.title.trim() || !this.form.message.trim()) return false;
      if (this.form.target_type === 'academic_year' && !this.form.academic_year_id) return false;
      return true;
    },

    targetLabel() {
      if (this.form.target_type === 'all') return 'كل الطلاب';
      const year = this.gradeStore.activeGrades.find(y => y.id === this.form.academic_year_id);
      return year ? `طلاب: ${year.name}` : 'سنة دراسية محددة';
    },
  },

  methods: {
    async confirmSend() {
      if (!this.canSend) return;

      const { isConfirmed } = await Swal.fire({
        title: 'تأكيد إرسال الإشعار',
        html: `
          <p><strong>العنوان:</strong> ${this.escapeHtml(this.form.title)}</p>
          <p><strong>النص:</strong> ${this.escapeHtml(this.form.message)}</p>
          <p><strong>المستهدفون:</strong> ${this.escapeHtml(this.targetLabel)}</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'إرسال',
        cancelButtonText: 'إلغاء',
      });
      if (!isConfirmed) return;

      this.sending = true;
      try {
        const { data } = await axios.post('/admin/student-notifications', {
          title: this.form.title,
          message: this.form.message,
          target_type: this.form.target_type,
          academic_year_id: this.form.target_type === 'academic_year' ? this.form.academic_year_id : null,
        });
        Swal.fire({ icon: 'success', title: data.message || 'تم الإرسال', timer: 2200, showConfirmButton: false });
        this.form = blankForm();
      } catch (e) {
        const msg = e?.response?.data?.message || Object.values(e?.response?.data?.errors || {})[0]?.[0] || 'حدث خطأ أثناء الإرسال';
        Swal.fire({ icon: 'error', title: msg, timer: 2500, showConfirmButton: false });
      } finally {
        this.sending = false;
      }
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
  },
};
</script>

<style scoped>
.ds-card--form {
  background: #fff;
  border: 1px solid var(--surface-border, #e5e7eb);
  border-radius: 14px;
  padding: 1.5rem;
  max-width: 640px;
}
.target-option {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-weight: 600;
}
</style>
